import { cancelNavMenuSwitchTracking } from '@/components/layout/Navigation/sidebar/nav-menu-switch-tracking';
import { useLatestRef } from '@/hooks/useLatestRef';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  parseWorkHomeObjectRoute,
  parseWorkHomeViewFromUrl,
  type ObjectWorkspaceRoute,
} from '@heidi/agents/contracts';
import { workChatProjectIdFromUrl } from '@heidi/agents/data';

const objectRouteFromUrl = (url: string): ObjectWorkspaceRoute | null => {
  const parsed = new URL(url, 'https://heidi.local');
  return parseWorkHomeObjectRoute({
    objectKind: parsed.searchParams.get('objectKind') ?? undefined,
    objectId: parsed.searchParams.get('objectId') ?? undefined,
    sourceSessionId: parsed.searchParams.get('sourceSessionId') ?? undefined,
  });
};

/** Landing object identity — home↔object and object A↔B unmount the composer. */
const isSameObjectLandingSurface = (
  currentAsPath: string,
  targetUrl: string
): boolean => {
  const current = objectRouteFromUrl(currentAsPath);
  const target = objectRouteFromUrl(targetUrl);
  if (current === null && target === null) return true;
  if (current === null || target === null) return false;
  return (
    current.kind === target.kind &&
    current.id === target.id &&
    current.sourceSessionId === target.sourceSessionId
  );
};

/** Same routed chat, or the same Work landing surface — query-only view or
 *  object-workspace pops unmount the home composer and must be held when a
 *  draft/queue is live. */
const isSameWorkChatSurface = (
  currentAsPath: string,
  targetUrl: string,
  locales: readonly string[] | undefined
): boolean => {
  const target = workChatProjectIdFromUrl(targetUrl, locales);
  const current = workChatProjectIdFromUrl(currentAsPath, locales);
  if (target === null || target !== current) return false;
  if (target !== undefined) return true;
  return (
    parseWorkHomeViewFromUrl(currentAsPath) === parseWorkHomeViewFromUrl(targetUrl) &&
    isSameObjectLandingSurface(currentAsPath, targetUrl)
  );
};

export type QueuedTurnDiscardReason = 'queued' | 'sending' | 'draft';

export interface QueuedTurnDiscardGuard {
  confirmReason: QueuedTurnDiscardReason | null;
  cancelDiscard: () => void;
  confirmDiscard: () => void;
  /** Runs a navigation owned by the active chat, such as its send handoff,
   *  without mistaking it for a user abandoning the chat. */
  runWithoutDiscardGuard: <Result>(navigate: () => Promise<Result>) => Promise<Result>;
  /** For teardowns this hook cannot see: a drill-in unmounts the chat without
   *  navigating, so there is no `routeChangeStart` to intercept. */
  requestDiscard: (proceed: () => void) => void;
  /** Whether a push to `url` would stop at the confirm dialog right now. */
  wouldHoldNavigation: (url: string) => boolean;
}

interface PendingDiscard {
  proceed: () => void;
  /** Set for a popstate: the address bar already moved, so Cancel restores it. */
  restoreUrl?: string;
  reason: QueuedTurnDiscardReason;
}

/** Arm the route guard for an owned navigation, then disarm on the next
 *  task if no `routeChangeStart` arrived — unless a replay is still in
 *  flight. Shared so `runWithoutDiscardGuard` and `confirmDiscard` stay in
 *  sync with `onRouteSettled`. */
const armForNavigation = (
  allowNextNavigationRef: { current: boolean },
  ownedNavigationInFlightRef: { current: boolean },
  events: {
    on: (event: 'routeChangeStart', handler: () => void) => void;
    off: (event: 'routeChangeStart', handler: () => void) => void;
  }
) => {
  allowNextNavigationRef.current = true;
  let sawRouteChangeStart = false;
  const onOwnedRouteChangeStart = () => {
    if (allowNextNavigationRef.current) {
      sawRouteChangeStart = true;
    }
  };
  events.on('routeChangeStart', onOwnedRouteChangeStart);
  const unlisten = () => {
    events.off('routeChangeStart', onOwnedRouteChangeStart);
  };
  return {
    abort: () => {
      allowNextNavigationRef.current = false;
      unlisten();
    },
    release: () => {
      // Keep listening until the deferred disarm: Next (and page-owned
      // `router.push` confirms) often emit `routeChangeStart` after `proceed`
      // returns but before this timeout (AGE-2470).
      setTimeout(() => {
        unlisten();
        if (!sawRouteChangeStart && !ownedNavigationInFlightRef.current) {
          allowNextNavigationRef.current = false;
        }
      }, 0);
    },
  };
};

/** Leaving a chat that still owes commands — a held queue, or an opening send in
 *  flight — asks first. */
export const useQueuedTurnDiscardGuard = ({
  hasQueuedTurns,
  isOpeningTurnInFlight = false,
  hasUnsentDraft = false,
  selectedProjectId,
  onDiscardConfirmed,
  shouldHoldNavigation,
}: {
  hasQueuedTurns: boolean;
  isOpeningTurnInFlight?: boolean;
  hasUnsentDraft?: boolean;
  selectedProjectId: string | undefined;
  /** Page-owned leave cleanup. Called once, before the pending navigation. */
  onDiscardConfirmed?: () => void;
  /** Override Work-project URL matching for a host that owns another chat route. */
  shouldHoldNavigation?: (url: string) => boolean;
}): QueuedTurnDiscardGuard => {
  const router = useRouter();
  const [pending, setPending] = useState<PendingDiscard | null>(null);
  const hasQueuedTurnsRef = useLatestRef(hasQueuedTurns);
  const isOpeningTurnInFlightRef = useLatestRef(isOpeningTurnInFlight);
  const hasUnsentDraftRef = useLatestRef(hasUnsentDraft);
  const selectedProjectIdRef = useLatestRef(selectedProjectId);
  const onDiscardConfirmedRef = useLatestRef(onDiscardConfirmed);
  const shouldHoldNavigationRef = useLatestRef(shouldHoldNavigation);
  // Set while replaying the navigation the user just answered: no re-prompt.
  const allowNextNavigationRef = useRef(false);
  // True once `replayNavigation` has started a push/replace that Next has
  // not yet announced. confirmDiscard's setTimeout(0) must not disarm
  // `allowNextNavigationRef` during that window (AGE-2470).
  const ownedNavigationInFlightRef = useRef(false);

  const holdReason = useCallback(
    (): QueuedTurnDiscardReason | null =>
      isOpeningTurnInFlightRef.current
        ? 'sending'
        : hasQueuedTurnsRef.current
          ? 'queued'
          : hasUnsentDraftRef.current
            ? 'draft'
            : null,
    [hasQueuedTurnsRef, hasUnsentDraftRef, isOpeningTurnInFlightRef]
  );

  const replayNavigation = useCallback(
    (url: string, mode: 'push' | 'replace') => {
      allowNextNavigationRef.current = true;
      ownedNavigationInFlightRef.current = true;
      void router[mode](url).catch(() => {
        allowNextNavigationRef.current = false;
        ownedNavigationInFlightRef.current = false;
      });
    },
    [router]
  );

  const shouldHold = useCallback(
    (url: string): QueuedTurnDiscardReason | null => {
      if (allowNextNavigationRef.current) return null;
      const reason = holdReason();
      if (!reason) return null;
      if (shouldHoldNavigationRef.current) {
        return shouldHoldNavigationRef.current(url) ? reason : null;
      }
      const target = workChatProjectIdFromUrl(url, router.locales);
      if (target === null || target !== selectedProjectIdRef.current) return reason;
      if (target !== undefined) return null;
      return isSameWorkChatSurface(router.asPath, url, router.locales) ? null : reason;
    },
    [holdReason, router, selectedProjectIdRef, shouldHoldNavigationRef]
  );

  const wouldHoldNavigation = useCallback(
    (url: string) => shouldHold(url) !== null,
    [shouldHold]
  );

  useEffect(() => {
    const onRouteChangeStart = (
      url: string,
      routeProps: { shallow: boolean } = { shallow: false }
    ) => {
      const reason = shouldHold(url);
      if (!reason) return;
      setPending({ proceed: () => replayNavigation(url, 'push'), reason });
      // Keep cancelled:true so Pages Router treats this as an expected abort (no
      // overlay) and RUM still classifies the message as abort_route_change.
      // The nav-switch checkpoint listener skips cancelTracking on cancelled
      // errors (rapid double-click semantics), so close the sidebar span here.
      cancelNavMenuSwitchTracking();
      const cancellationError = Object.assign(new Error('Route change aborted.'), {
        cancelled: true,
      });
      router.events.emit('routeChangeError', cancellationError, url, routeProps);
      // Pages Router has no cancel API, so the handler still throws. Its change()
      // path treats a cancelled Error as an expected false result instead of
      // rejecting the navigation promise into the development error overlay.
      throw cancellationError;
    };
    const onRouteSettled = () => {
      allowNextNavigationRef.current = false;
      ownedNavigationInFlightRef.current = false;
    };

    router.events.on('routeChangeStart', onRouteChangeStart);
    router.events.on('routeChangeComplete', onRouteSettled);
    router.events.on('routeChangeError', onRouteSettled);
    return () => {
      router.events.off('routeChangeStart', onRouteChangeStart);
      router.events.off('routeChangeComplete', onRouteSettled);
      router.events.off('routeChangeError', onRouteSettled);
    };
  }, [replayNavigation, router, shouldHold]);

  useEffect(() => {
    router.beforePopState(({ as }) => {
      if (allowNextNavigationRef.current) return true;
      const reason = holdReason();
      if (!reason) return true;
      const shouldHold = shouldHoldNavigationRef.current
        ? shouldHoldNavigationRef.current(as)
        : !isSameWorkChatSurface(router.asPath, as, router.locales);
      if (!shouldHold) return true;
      setPending({
        // The pop already moved the address bar; a push would leave the entry the
        // user just left in front of them.
        proceed: () => replayNavigation(as, 'replace'),
        restoreUrl: router.asPath,
        reason,
      });
      return false;
    });
    return () => router.beforePopState(() => true);
  }, [
    holdReason,
    replayNavigation,
    router,
    selectedProjectIdRef,
    shouldHoldNavigationRef,
  ]);

  useEffect(() => {
    if (!pending) return;
    if (isOpeningTurnInFlight || hasQueuedTurns || hasUnsentDraft) return;
    setPending(null);
  }, [hasQueuedTurns, hasUnsentDraft, isOpeningTurnInFlight, pending]);

  const cancelDiscard = useCallback(() => {
    const restoreUrl = pending?.restoreUrl;
    setPending(null);
    if (restoreUrl && restoreUrl !== router.asPath) {
      allowNextNavigationRef.current = true;
      void router.replace(restoreUrl).catch(() => {
        allowNextNavigationRef.current = false;
      });
    }
  }, [pending, router]);

  const requestDiscard = useCallback(
    (proceed: () => void) => {
      const reason = holdReason();
      if (!reason) {
        proceed();
        return;
      }
      setPending({ proceed, reason });
    },
    [holdReason]
  );

  const runWithoutDiscardGuard = useCallback(
    async <Result>(navigate: () => Promise<Result>): Promise<Result> => {
      const armed = armForNavigation(
        allowNextNavigationRef,
        ownedNavigationInFlightRef,
        router.events
      );
      try {
        return await navigate();
      } catch (error) {
        armed.abort();
        throw error;
      } finally {
        armed.release();
      }
    },
    [router.events]
  );

  const confirmDiscard = useCallback(() => {
    const target = pending;
    setPending(null);
    if (!target) return;
    onDiscardConfirmedRef.current?.();
    // A drill-in / Home / New chat confirm often navigates only in `proceed`.
    // That push is not a replay, so arm here or the route guard asks again
    // before React can drop the draft/queue (AGE-2470).
    const armed = armForNavigation(
      allowNextNavigationRef,
      ownedNavigationInFlightRef,
      router.events
    );
    try {
      target.proceed();
    } catch (error) {
      armed.abort();
      throw error;
    } finally {
      armed.release();
    }
  }, [onDiscardConfirmedRef, pending, router.events]);

  return {
    confirmReason: pending?.reason ?? null,
    cancelDiscard,
    confirmDiscard,
    runWithoutDiscardGuard,
    requestDiscard,
    wouldHoldNavigation,
  };
};
